import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { IconContext } from 'react-icons';
import { FiBook } from 'react-icons/fi';
import { Card, Divider, Typography, SelectField, CardHeader, CardTitle } from '@otcmarketsgroup/otcm-ui-lib';
import { getUserManual } from '../../api/bluesky';
import { jurisdictionOptions } from '../../constants/jurisdictionOptions';
import { withRouter } from '../../components/WithRouter';
import { BackButton } from '../../components/BackButton';
import { Loading } from '../../components/Loading';
import styles from './BlueSkyRules.module.scss';
import { ROUTES } from '../../constants/routes';
import { storage } from '../../constants';


const BlueSkyRules = ({ params, navigate }) => {
    const jurisdiction = params?.jurisdiction;
    const [rulesData, setRulesData] = useState([]);
    const [rulesDataError, setRulesDataError] = useState(null);
    const [reloading, setReloading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const hash = window.location.hash;
    const symbol = window.sessionStorage.getItem(storage.SYMBOL);

    useEffect(() => {
        setReloading(true);
        setDataLoaded(false);
        getUserManual(jurisdiction)
            .then(data => {
                setReloading(false);
                setRulesData(data);
                setDataLoaded(true);
            })
            .catch(err => {
                console.error(err);
                setReloading(false);
                setRulesDataError(err?.message || 'Error fetching data.');
                setDataLoaded(true);
            });
    }, [jurisdiction]);

    useEffect(() => {
        if (hash && hash.length > 1) {
            const el = document.getElementById(hash.substring(1));
            if (el) {
                setTimeout(() => { el.scrollIntoView(true); }, 10);
            }
        }
    }, [hash]);

    const onSelect = e => {
        const { value } = e.target;
        const path = `${ROUTES.blueSkyRulesPath}/${value}`;
        navigate(path);
    };

    const serialize = (item, index) => {
        switch (item.type) {
            case 'html': return <span key={index} dangerouslySetInnerHTML={{ __html: item.text }} />;
            case 'Regular': return <span key={index} className={styles[item.color]} dangerouslySetInnerHTML={{ __html: item.text }} />;
            case 'Bold': return <strong key={index} className={styles[item.color]}>{item.text}</strong>;
            case 'Italic': return <em key={index} className={styles[item.color]}>{item.text}</em>;
            case 'Inline': return item.nodes.map((node, i) => serialize(node, i));
            case 'OrderedList': return <ol key={index} type={item.listType} start={item.start} className={cn({
                [styles.noPadding]: item.noPadding
            })}>
                {item.nodes.map((node, i) => {
                    if (node.type === 'OrderedList') return serialize(node, i);
                    return <li key={i}>{serialize(node, i)}</li>;
                })}
            </ol>;
            case 'Break': return <br key={index} />;
        }
    };

    const renderContent = content => {
        return content.map((c, i) => <div key={i}>{serialize(c, i)}</div>);
    };

    return <div className={styles.container}>
        <BackButton to={`${ROUTES.blueSkyMonitorPath}/${symbol}`} />
        <Card>
            <CardHeader>
                <CardTitle icon={<FiBook />}>
                    Blue Sky Rules
                </CardTitle>
                <div>
                    <SelectField
                        type='secondary'
                        disabled={reloading}
                        onChange={onSelect}
                        options={jurisdictionOptions}
                        value={params?.jurisdiction}
                    />
                </div>
            </CardHeader>
            <Divider size='large' color='green' />
            <Loading
                error={rulesDataError}
                emptyMessage='No commissions found'
                loaded={dataLoaded}
                data={rulesData}>
                <div className={styles.jumpLinks}>
                    {rulesData.length > 0 && rulesData.map((section, i) => {
                        if (section.hideJumpLink) return;
                        return (
                            <React.Fragment key={`jump-link${i}`}>
                                {section.linkNewLine && (
                                    <>
                                        <br />
                                        <br />
                                    </>
                                )}
                                <a
                                    href={`#${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                                    className={cn(styles.linkItem, {
                                        [styles.boldLink]: section.linkBold,
                                        [styles.lastLine]: i !== rulesData.length - 1 && rulesData[i + 1].linkNewLine
                                    })}
                                >
                                    {section.jumpLinkTitle || section.title}
                                </a>
                            </React.Fragment>
                        );
                    })}

                </div>
                <div>
                    {rulesData.length > 0 && rulesData.map((section, i) =>
                        <div key={i} className={styles.rulesBlock}>
                            <div id={section.title.replace(/[\W_]+/g, ' ').trim().replace(/ /g, '-').toLowerCase()} className={styles.sectionTitle}>
                                <Typography variant='h4'>
                                    {section.title}
                                </Typography>
                                <Divider size='large' color='green' />
                            </div>
                            <div key='mainContent' className={styles.mainContent}>
                                {renderContent(section.nodes)}
                            </div>
                        </div>
                    )}
                </div>
            </Loading>
        </Card>
    </div>;
};

BlueSkyRules.propTypes = {
    params: PropTypes.object,
    navigate: PropTypes.func
};

export default withRouter(BlueSkyRules);